import {
  Dispatch, SetStateAction, useMemo, useRef, useEffect,
} from 'react';
import { GlobalFilterObject } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { useResumeSearchAlerts } from '@hooks/filters/useResumeSearchAlerts';
import { Button, Tooltip } from '@components/common';
import { ArrowUturnUpIcon } from '@heroicons/react/16/solid';
import { sortObjectKeys } from '@utils/objects/sortObjectKeys';
import { i18nConfig } from 'i18n-config';
import { TopBarDropdownSuggestion } from './TopBarDropdownSuggestion';
import { lastActivity } from '../helpers/filterObjects';
import { filterStyles } from '../helpers/filterStyles';


// Interface
// ******************************************
interface TopBarSuggestedFiltersProps {
  queryParams: object,
  setQueryParams: Dispatch<SetStateAction<object>>,
  sortQueryParams?: boolean,
  activateForParams?: string[],
  jobSlug?: string,
  locale?: Locale,
}

interface FilterObject {
  label: string,
  param: object,
  multiple: boolean,
}


// Talents Recommended Filters
// ******************************************
const talentsRecommendedFilters: FilterObject[] = [
  { // Candidate name
    label: 'global.filter.candidate-name',
    param: { nameSearch: 1 },
    multiple: false,
  },
  { // Latest experience
    label: 'resume.filter.label.experience-last',
    param: { presentExperience: 1 },
    multiple: false,
  },
  { // My applicants only
    label: 'global.filter.only-my-applicants',
    param: { searchInApplicants: 1 },
    multiple: false,
  },
  { // Last activity
    label: 'global.active-in.label',
    param: lastActivity,
    multiple: true,
  },
];


// Applicants Recommended Filters (job applications list)
// ******************************************
const applicantsRecommendedFilters: FilterObject[] = [
  { // Recommended by agent
    label: 'resume.filter.label.recommendation',
    param: { recommendedByAgent: 1 },
    multiple: false,
  },
];


/**
 * @description The top bar suggested filters component. It shows a list of suggested filters.
 * - **queryParams** The query params object
 * - **setQueryParams** The set query params function (get it from parent)
 * - **activateForParams** The query params that should activate the component. Defaults to ['keyword', 'similarSearch']
 * When the query params match the activateForParams, the component will show the suggested filters.
 * - **jobSlug** The job slug. If present, the component will show the applicants recommended filters.
 * - **sortQueryParams** If true, the query params will be sorted alphabetically. Defaults to true.
 */
export const TopBarSuggestedFilters = (props: TopBarSuggestedFiltersProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    activateForParams = ['keyword', 'similarSearch'],
    jobSlug,
    sortQueryParams = true,
    locale = i18nConfig.defaultLocale,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Detect if all the query params match the activateForParams
  // If there are more query params than the activateForParams, then the component should not be activated
  // ***********************************************
  const showSuggestions = useMemo(
    () => Object.keys(queryParams).length > 0 && Object.keys(queryParams).every((key) => activateForParams.includes(key)),
    [queryParams, activateForParams],
  );


  // Apply the selected suggestion
  // ***********************************************
  const applySuggestion = (filter: object) => {
    setQueryParams((prev) => {
      const newQueryParams = { ...prev, ...filter };
      return sortQueryParams ? sortObjectKeys(newQueryParams) : newQueryParams;
    });
  };


  // Get the recommended city that matches the keyword from search alerts
  // ***********************************************
  const { matchingLocations } = useResumeSearchAlerts({ locale });
  const safeLocations = useRef(matchingLocations);


  // Get the matching city
  // ***********************************************
  const matchingCity = useMemo(
    () => {
      const locations = safeLocations.current;
      const foundCity = locations?.find((location) => location.keyword === queryParams?.['keyword' as keyof typeof queryParams]);
      if (!foundCity) return undefined;
      // Return the city suggestion as a filter object
      return {
        label: `${foundCity.location}${foundCity.radiusInKm ? `, +${foundCity.radiusInKm} km` : ''}`,
        param: {
          location: foundCity.location,
          ...(foundCity.radiusInKm ? { radiusInKm: foundCity.radiusInKm } : {}),
        },
        multiple: false,
      };
    },
    [queryParams],
  );


  // Update the safeLocations ref. Keep in mind that when we choose a keyword from the saved search alerts, the search alerts will be updated.
  // That means that we will lose all the other query params. That's why we're using a ref, and we update only when we cannot show suggestions.
  // ***********************************************
  useEffect(() => {
    if (!showSuggestions) {
      safeLocations.current = matchingLocations;
    }
  }, [matchingLocations, showSuggestions]);


  // Choose the right set of filters recommendations. Add the city suggestion if it exists.
  // ***********************************************
  const recommendedFilters = useMemo(() => {
    if (jobSlug) {
      return applicantsRecommendedFilters;
    }

    const filters = [...talentsRecommendedFilters];
    if (matchingCity) {
      filters.push(matchingCity);
    }
    return filters;
  }, [jobSlug, matchingCity]);


  // Return null if the component should not be activated
  // ***********************************************
  if (!showSuggestions) return null;


  // Render component
  // ***********************************************
  return (
    <>
      {
        recommendedFilters.map((filter) => {
          // If the filter is not multiple, show a simple button
          if (!filter.multiple) {
            return (
              <Tooltip
                floating
                key={filter.label}
                position="bottom"
                className="block"
                contentSizeClass={filterStyles.suggestionTooltip}
                tooltipText={t('resume.filter.apply.suggestion')}
              >
                <Button
                  styling="none"
                  onClick={() => applySuggestion(filter.param)}
                  className={filterStyles.suggestionButton}
                >
                  <ArrowUturnUpIcon className={`${filterStyles.suggestionButtonIcon} size-4`} />
                  {t(filter.label)}
                </Button>
              </Tooltip>
            );
          }

          // If the filter is multiple, show a dropdown
          return (
            <TopBarDropdownSuggestion
              key={filter.label}
              data={filter.param as GlobalFilterObject}
              label={filter.label}
              applySuggestion={applySuggestion}
            />
          );
        })
      }
    </>
  );
};
